// Definition-driven filter system (ported from the admin app).
// A single source of truth: URL params, filter state, API params and
// client-side filtering are all derived from these definitions.

export const DEFAULT_FILTER_DEFS = [
  {
    id: 'status', label: 'Status', type: 'multistatus', key: 'status',
    options: [
      { label: 'Active',      value: 'A'   },
      { label: 'New',         value: 'New' },
      { label: 'Sold',        value: 'Sld' },
      { label: 'Coming Soon', value: 'Cs'  },
      { label: 'Off-Market',  value: 'U'   },
    ],
  },
  {
    id: 'propertyType', label: 'Property Type', type: 'checkbox', key: 'propertyType',
    options: [
      { label: 'Houses (Single Family)', value: 'single-family' },
      { label: 'Townhouse', value: 'townhouse' },
      { label: 'Condos', value: 'condos' },
      { label: 'Co-ops', value: 'co-ops' },
      { label: 'Lots / Land', value: 'lots-land' },
      { label: 'Multi Family', value: 'multi-family' },
      { label: 'Apartments', value: 'apartments' },
    ],
  },
  {
    id: 'price', label: 'Price', type: 'range', keys: ['minPrice', 'maxPrice'],
    placeholders: ['$0', 'No max'], inputType: 'number', thousands: true,
  },
  {
    id: 'beds', label: 'Bedrooms', type: 'select', key: 'minBeds',
    options: [{ label: 'Any', value: '' }, { label: '1+', value: '1' }, { label: '2+', value: '2' }, { label: '3+', value: '3' }, { label: '4+', value: '4' }, { label: '5+', value: '5' }],
  },
  {
    id: 'baths', label: 'Bathrooms', type: 'select', key: 'minBaths',
    options: [{ label: 'Any', value: '' }, { label: '1+', value: '1' }, { label: '2+', value: '2' }, { label: '3+', value: '3' }, { label: '4+', value: '4' }],
  },
  {
    id: 'sqft', label: 'Sq Ft', type: 'range', keys: ['minSqft', 'maxSqft'],
    placeholders: ['Min', 'Max'], inputType: 'number', thousands: true,
  },
  {
    id: 'yearBuilt', label: 'Year Built', type: 'range', keys: ['minYearBuilt', 'maxYearBuilt'],
    placeholders: ['Min', 'Max'], inputType: 'number',
  },
  {
    id: 'dom', label: 'Days on Market', type: 'range', keys: ['minDom', 'maxDom'],
    placeholders: ['Min', 'Max'], inputType: 'number',
  },
  {
    id: 'garage', label: 'Garage Spaces', type: 'select', key: 'minGarage',
    options: [{ label: 'Any', value: '' }, { label: '1+', value: '1' }, { label: '2+', value: '2' }, { label: '3+', value: '3' }],
  },
  { id: 'city', label: 'City / Area', type: 'location_autocomplete', key: 'city' },
  { id: 'zip', label: 'Zip Code', type: 'zip_autocomplete', key: 'zip' },
  { id: 'neighborhood', label: 'Neighborhood', type: 'text', key: 'neighborhood' },
  { id: 'subdivision', label: 'Subdivision', type: 'text', key: 'subdivision' },
  {
    id: 'listingType', label: 'Listing Type', type: 'checkbox', key: 'listingType',
    options: [{ label: 'Sale', value: 'Sale' }, { label: 'Rent', value: 'Rent' }],
  },
];

// Optional extra fields the visitor can add to the panel (categorized).
export const EXTRA_FILTER_DEFS = [
  { id: 'state', category: 'Location', label: 'State', type: 'text', key: 'state', removable: true },

  { id: 'levels', category: 'Property', label: 'Levels', type: 'checkbox', key: 'levels', removable: true,
    options: [{ label: 'One', value: 'One' }, { label: 'Two', value: 'Two' }, { label: 'Three+', value: 'Three Or More' }] },
  { id: 'propertyCondition', category: 'Property', label: 'Property Condition', type: 'multicheck', key: 'propertyCondition', removable: true,
    options: ['Resale', 'New Construction', 'Updated/Remodeled', 'Fixer'] },
  { id: 'architecturalStyle', category: 'Property', label: 'Architectural Style', type: 'multicheck', key: 'architecturalStyle', removable: true,
    options: ['Contemporary', 'Mediterranean', 'Colonial', 'Ranch', 'Modern', 'Traditional', 'Spanish', 'Old Spanish', 'Victorian', 'Art Deco'] },
  { id: 'constructionMaterials', category: 'Property', label: 'Construction Materials', type: 'multicheck', key: 'constructionMaterials', removable: true,
    options: ['Block', 'CBS', 'Brick', 'Frame', 'Concrete', 'Stucco', 'Vinyl Siding', 'Wood Siding'] },
  { id: 'roof', category: 'Property', label: 'Roof', type: 'multicheck', key: 'roof', removable: true,
    options: ['Tile', 'Metal', 'Shingle', 'Flat', 'Concrete', 'Spanish Tile', 'Barrel'] },

  { id: 'lotSize', category: 'Lot', label: 'Lot Size (sqft)', type: 'range', keys: ['minLotSqft', 'maxLotSqft'], placeholders: ['Min', 'Max'], inputType: 'number', removable: true },
  { id: 'lotFeatures', category: 'Lot', label: 'Lot Features', type: 'multicheck', key: 'lotFeatures', removable: true,
    options: ['Corner Lot', 'Cul-De-Sac', 'Oversized Lot', 'Irregular Lot', 'Zero Lot Line', 'On Golf Course'] },
  { id: 'view', category: 'Lot', label: 'View', type: 'multicheck', key: 'view', removable: true,
    options: ['Water', 'Pool', 'Garden', 'City', 'Golf Course', 'Ocean', 'Lake', 'Intracoastal', 'Canal', 'Preserve'] },

  { id: 'poolPrivate', category: 'Pool & Spa', label: 'Private Pool', type: 'checkbox', key: 'hasPool', removable: true, options: [{ value: 'true', label: 'Private Pool Only' }] },
  { id: 'poolFeatures', category: 'Pool & Spa', label: 'Pool Features', type: 'multicheck', key: 'poolFeatures', removable: true,
    options: ['In Ground', 'Heated', 'Salt Water', 'Screen Enclosure', 'Lap', 'Infinity', 'Gunite', 'Fiberglass', 'Solar Heat'] },
  { id: 'spa', category: 'Pool & Spa', label: 'Spa', type: 'boolean', key: 'spaYn', removable: true },

  { id: 'waterfront', category: 'Waterfront', label: 'Waterfront', type: 'boolean', key: 'waterfront', removable: true },
  { id: 'waterfrontFeatures', category: 'Waterfront', label: 'Waterfront Features', type: 'multicheck', key: 'waterfrontFeatures', removable: true,
    options: ['Ocean Front', 'Ocean Access', 'Lake Front', 'Lake Access', 'Canal Front', 'Canal Access', 'Bayfront', 'Bay Access', 'Intracoastal', 'Intracoastal Access', 'Navigable Water', 'River Front', 'River Access', 'Seawall', 'No Fixed Bridges', 'Dock', 'Boat Lift', 'Private Dock', 'Beach Access', 'Beach Front', 'Pond'] },

  { id: 'cooling', category: 'Interior', label: 'Cooling', type: 'multicheck', key: 'cooling', removable: true,
    options: ['Central Air', 'Zoned', 'Ductless', 'Wall/Window Unit(s)', 'Electric', 'None'] },
  { id: 'heating', category: 'Interior', label: 'Heating', type: 'multicheck', key: 'heating', removable: true,
    options: ['Central', 'Electric', 'Gas', 'Heat Pump', 'Solar', 'None'] },
  { id: 'flooring', category: 'Interior', label: 'Flooring', type: 'multicheck', key: 'flooring', removable: true,
    options: ['Tile', 'Carpet', 'Hardwood', 'Laminate', 'Vinyl', 'Marble', 'Concrete', 'Wood'] },
  { id: 'interiorFeatures', category: 'Interior', label: 'Interior Features', type: 'multicheck', key: 'interiorFeatures', removable: true,
    options: ['Walk-In Closet(s)', 'Vaulted Ceiling(s)', 'Kitchen Island', 'Pantry', 'Built-in Features', 'Elevator', 'Wet Bar', 'Roman Tub'] },
  { id: 'furnished', category: 'Interior', label: 'Furnished', type: 'checkbox', key: 'furnished', removable: true,
    options: [{ label: 'Furnished', value: 'Furnished' }, { label: 'Partially', value: 'Partially' }, { label: 'Unfurnished', value: 'Unfurnished' }] },
  { id: 'appliances', category: 'Interior', label: 'Appliances', type: 'multicheck', key: 'appliances', removable: true,
    options: ['Dishwasher', 'Dryer', 'Microwave', 'Refrigerator', 'Washer', 'Ice Maker', 'Gas Range', 'Electric Range', 'Oven'] },

  { id: 'exteriorFeatures', category: 'Exterior', label: 'Exterior Features', type: 'multicheck', key: 'exteriorFeatures', removable: true,
    options: ['Balcony', 'Patio', 'Deck', 'Outdoor Kitchen', 'Summer Kitchen', 'Storage', 'Tennis Court(s)', 'Outdoor Shower'] },
  { id: 'parkingFeatures', category: 'Parking', label: 'Parking Features', type: 'multicheck', key: 'parkingFeatures', removable: true,
    options: ['Attached', 'Detached', 'Driveway', 'Covered', 'Guest', 'Assigned', 'Circular Driveway', 'RV Access/Parking', 'EV Charging'] },
  { id: 'fencing', category: 'Exterior', label: 'Fencing', type: 'multicheck', key: 'fencing', removable: true,
    options: ['Fenced', 'Privacy', 'Chain Link', 'Wood', 'Vinyl', 'Electric'] },

  { id: 'associationYn', category: 'Community', label: 'HOA', type: 'boolean', key: 'associationYn', removable: true },
  { id: 'associationFee', category: 'Community', label: 'HOA Fee', type: 'range', keys: ['minHoaFee', 'maxHoaFee'], placeholders: ['Min', 'Max'], inputType: 'number', removable: true },
  { id: 'associationAmenities', category: 'Community', label: 'Association Amenities', type: 'multicheck', key: 'associationAmenities', removable: true,
    options: ['Pool', 'Clubhouse', 'Fitness Center', 'Tennis Court(s)', 'Gated', 'Playground', 'Sauna', 'Marina', 'Golf Course'] },
  { id: 'gatedCommunity', category: 'Community', label: 'Gated Community', type: 'boolean', key: 'gatedYn', removable: true },
  { id: 'seniorCommunity', category: 'Community', label: 'Senior Community (55+)', type: 'boolean', key: 'seniorYn', removable: true },
  { id: 'petsAllowed', category: 'Community', label: 'Pets Allowed', type: 'multicheck', key: 'petsAllowed', removable: true,
    options: ['Yes', 'No', 'Cats OK', 'Dogs OK', 'Size Limit', 'Breed Restrictions'] },

  { id: 'securityFeatures', category: 'Security', label: 'Security Features', type: 'multicheck', key: 'securityFeatures', removable: true,
    options: ['Smoke Detector(s)', 'Security System', 'Security Guard', 'Gated with Guard', 'Fire Sprinkler System', 'Key Card Entry', 'CCTV'] },

  { id: 'listingTerms', category: 'Financial', label: 'Listing Terms', type: 'multicheck', key: 'listingTerms', removable: true,
    options: ['Cash', 'Conventional', 'FHA', 'VA Loan', '1031 Exchange', 'Assumable', 'Owner Will Carry'] },
  { id: 'taxAnnual', category: 'Financial', label: 'Annual Tax', type: 'range', keys: ['minTax', 'maxTax'], placeholders: ['Min', 'Max'], inputType: 'number', removable: true },

  { id: 'greenEnergy', category: 'Green', label: 'Green Energy Efficient', type: 'multicheck', key: 'greenEnergy', removable: true,
    options: ['Solar', 'Energy Star Appliances', 'Insulation', 'HVAC', 'Windows', 'Water Heater'] },

  { id: 'agent', category: 'Agent', label: 'Agent / Member Name', type: 'text', key: 'agent', removable: true, placeholder: 'e.g. John Smith or agent ID' },
  { id: 'brokerage', category: 'Agent', label: 'Brokerage Name', type: 'text', key: 'brokerage', removable: true, placeholder: 'e.g. ABC Realty' },
  { id: 'officeId', category: 'Agent', label: 'Office ID', type: 'text', key: 'officeId', removable: true, placeholder: 'e.g. 3210904' },

  { id: 'stormProtection', category: 'Storm', label: 'Storm Protection', type: 'multicheck', key: 'stormProtection', removable: true,
    options: ['Impact Glass', 'Accordion Shutters', 'Roll-Down Shutters', 'Panel Shutters', 'Electric/Power Shutters', 'Generator'] },
];

export const EXTRA_BY_ID = Object.fromEntries(EXTRA_FILTER_DEFS.map((d) => [d.id, d]));

const ALL_DEFS = [...DEFAULT_FILTER_DEFS, ...EXTRA_FILTER_DEFS];

// Every known def (default + extra). Used for state/URL serialization and chips.
export function getAllDefs() {
  return ALL_DEFS;
}

// Kept for backwards compatibility; returns every known def.
export function getEffectiveFilterDefs() {
  return ALL_DEFS;
}

// Defs currently visible in the panel: defaults always + selected extras, ordered.
export function getVisibleDefs(extraFields = [], fieldOrder = []) {
  const extras = extraFields.map((id) => EXTRA_BY_ID[id]).filter(Boolean);
  let list = [...DEFAULT_FILTER_DEFS, ...extras];
  if (fieldOrder && fieldOrder.length) {
    const byId = Object.fromEntries(list.map((d) => [d.id, d]));
    const ordered = fieldOrder.map((id) => byId[id]).filter(Boolean);
    const missing = list.filter((d) => !fieldOrder.includes(d.id));
    list = [...ordered, ...missing];
  }
  return list;
}

// Extra defs grouped by category for the "add filter" drawer.
export function getExtraCategories() {
  const groups = {};
  EXTRA_FILTER_DEFS.forEach((d) => {
    const c = d.category || 'Other';
    (groups[c] = groups[c] || []).push(d);
  });
  return groups;
}

export function isArrayDef(def) {
  return def.type === 'checkbox' || def.type === 'multistatus' || def.type === 'multicheck';
}

// Flat list of every filter state key across all defs.
export function getAllKeys(defs = ALL_DEFS) {
  const keys = [];
  defs.forEach((d) => {
    if (d.keys) keys.push(...d.keys);
    else if (d.key) keys.push(d.key);
  });
  return keys;
}

export function getDefByKey(key, defs = ALL_DEFS) {
  return defs.find((d) => d.key === key || (d.keys && d.keys.includes(key))) || null;
}

export function getDefById(id, defs = ALL_DEFS) {
  return defs.find((d) => d.id === id) || null;
}

export function optionLabel(def, value) {
  if (def && def.options) {
    for (const opt of def.options) {
      const ov = opt && typeof opt === 'object' ? opt.value : opt;
      if (String(ov) === String(value)) return opt && typeof opt === 'object' ? (opt.label != null ? opt.label : ov) : opt;
    }
  }
  return value;
}
